// Simple immediate event sending - beacon first, fetch fallback
class ShopifyBehavioralTracker {
    constructor() {
        this.backendUrl = "https://simple-backend-3av3.vercel.app"
        this.uid = this.getOrCreateUID();
        this.sessionId = this.getOrCreateSessionId();
        this.platformIds = this.grabPlatformIds();
        this.init();
    }

    init() {
        // Track session start ONLY once per browser session
        this.trackSessionStart();
    }

    // ===========================================
    // CORE TRACKING METHOD
    // ===========================================

    track(eventName, properties = {}) {
        const now = Date.now();

        // Special handling for critical single-fire events
        const singleFireEvents = ['session_start'];
        if (singleFireEvents.includes(eventName)) {
            const singleFireKey = `single_fire_${eventName}`;
            if (sessionStorage.getItem(singleFireKey)) {
                return; // Already fired this session
            }
            sessionStorage.setItem(singleFireKey, 'true');
        }

        // Enhanced duplicate detection with meaningful re-engagement
        const eventKey = `${eventName}_${window.location.pathname}`;
        const eventCountKey = `count_${eventKey}`;
        const eventTimesKey = `times_${eventKey}`;
        const lastEventTimeKey = `last_${eventKey}`;

        // Get previous data
        const lastEventTime = parseInt(sessionStorage.getItem(lastEventTimeKey) || '0');
        let eventCount = parseInt(sessionStorage.getItem(eventCountKey) || '0');
        let eventTimes = JSON.parse(sessionStorage.getItem(eventTimesKey) || '[]');

        // Define minimum intervals for re-engagement (in milliseconds)
        const reEngagementIntervals = {
            'scroll_milestone': 0,
            'add_to_cart': 500,
            'initiate_checkout': 3000,
            'purchase': 10000,
            'email_captured': 1000,
            'sustainability_engaged': 30000,
            'soil_to_soil_engaged': 30000,
            'social_proof_engaged': 5000,
            'high_product_engagement': 30000,
            'video_watched': 500,
            'search_performed': 1000,
            'session_start': 0,
            'default': 1000
        };

        const minInterval = reEngagementIntervals[eventName] || reEngagementIntervals.default;
        const timeSinceLastEvent = now - lastEventTime;
        const shouldTrack = !lastEventTime || timeSinceLastEvent >= minInterval;

        if (!shouldTrack) {
            return; // Skip tracking if too soon since last event
        }

        // Update counters
        eventCount++;
        eventTimes.push(now);

        // Keep only last 10 timestamps
        if (eventTimes.length > 10) {
            eventTimes = eventTimes.slice(-10);
        }

        // Store updated data
        sessionStorage.setItem(eventCountKey, eventCount.toString());
        sessionStorage.setItem(eventTimesKey, JSON.stringify(eventTimes));
        sessionStorage.setItem(lastEventTimeKey, now.toString());

        // Only include metadata for session_start
        const includeMetadata = ['session_start'].includes(eventName);
        let dougStamper = now;
        let eventId = `${this.sessionId}_${eventName}_${dougStamper}`

        const eventData = {
            uid: this.uid,
            session_id: this.sessionId,
            event_id: eventId,
            event_name: eventName,
            event_data: {
                ...properties,
                event_count: eventCount,
                time_since_last: lastEventTime ? Math.floor(timeSinceLastEvent / 1000) : 0,
                ...(includeMetadata ? {
                    url: window.location.href,
                    referrer: document.referrer,
                    user_agent: navigator.userAgent,
                    timestamp: dougStamper
                } : {
                    timestamp: dougStamper
                })
            },
            platform_ids: includeMetadata ? {
                ...this.platformIds,
                ...this.getUTMParams()
            } : {}
        };

        // Send immediately - beacon first, fetch fallback
        this.sendEventImmediately(eventData);

        // Send to Meta Pixel and GA4
        this.sendToMetaPixel(eventName, properties, eventId);
        this.sendToGA4(eventName, properties);
    }

    // ===========================================
    // IMMEDIATE EVENT SENDING - BEACON FIRST
    // ===========================================

    async sendEventImmediately(eventData) {
        const serverEventData = {...eventData};
        if (serverEventData.event_name === "email_captured") {
            serverEventData.event_name = "Subscribe";
        }else if (serverEventData.event_name === "search_performed"){
            serverEventData.event_name = "Search";
        }

        const payload = JSON.stringify([serverEventData]); // Backend expects array format

        try {
            // PRIMARY: Try sendBeacon first (most reliable, but no cookies support)
            if (navigator.sendBeacon) {
                const blob = new Blob([payload], { type: 'application/json' });
                const success = navigator.sendBeacon(`${this.backendUrl}/api/events`, blob);

                if (success) {
                    return; // Success - exit early
                }
            }

            // FALLBACK: Use fetch with credentials
            const response = await fetch(`${this.backendUrl}/api/events`, {
                method: 'POST',
                mode: 'cors',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: payload,
                keepalive: true
            });

            if (!response.ok) {
                throw new Error(`Event failed: ${response.status} ${response.statusText}`);
            }


        } catch (error) {
            // Event is lost - no queue, no retries
        }
    }

    // ===========================================
    // USER IDENTIFICATION
    // ===========================================

    async identify(email) {
        const oldUID = this.uid;
        // this.uid = email;
        localStorage.setItem('ecommerce_uid', email);
        localStorage.setItem('user_email', email);

        // Send identify request to backend
        try {
            await fetch(`${this.backendUrl}/api/identify`, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    uid: email,
                    email: email,
                    anonymous_id: oldUID
                }),
                keepalive: true
            });
        } catch (error) {
            // Silent fail
        }

        // Enrich customer in Klaviyo
        this.enrichKlaviyoProfile(email);
    }

    // ===========================================
    // KLAVIYO CUSTOMER ENRICHMENT (Client-Side)
    // ===========================================

    async enrichKlaviyoProfile(email) {
        if (!email || !email.includes('@')) return;

        // Check if Klaviyo is loaded
        if (typeof _learnq === 'undefined') {
            console.warn('Klaviyo not loaded');
            return;
        }

        // Prepare enrichment properties
        const properties = {
            // Session & tracking data
            $session_id: this.sessionId,
            $uid: this.uid,
            
            // Device & browser info
            $device_type: this.getDeviceType(),
            $screen_width: window.screen.width,
            $screen_height: window.screen.height,
            $viewport_width: window.innerWidth,
            $viewport_height: window.innerHeight,
            $timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            $language: navigator.language,
            $platform: (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || '',
            
            // Traffic source
            $referrer: document.referrer,
            $referrer_domain: this.getReferrerDomain(),
            $current_url: window.location.href,
            
            // Platform IDs (valuable for attribution)
            $fbp: this.platformIds.fbp || '',
            $fbc: this.platformIds.fbc || '',
            $gclid: this.platformIds.gclid || '',
            $ttclid: this.platformIds.ttclid || '',
            
            // Timestamps
            $last_active: new Date().toISOString(),
            $enriched_at: new Date().toISOString()
        };

        // Add UTM parameters if they exist
        const utmParams = this.getUTMParams();
        Object.keys(utmParams).forEach(key => {
            properties[`${key}`] = utmParams[key];
        });

        try {
            // Use Klaviyo's identify method (requires Klaviyo.js to be loaded)
            _learnq.push(['identify', {
                '$email': email,
                ...properties
            }]);

            // Also track as a custom event for better visibility
            _learnq.push(['track', 'Profile Enriched', {
                email: email,
                enrichment_source: 'behavioral_tracker',
                ...properties
            }]);
        } catch (error) {
            // Silent fail - don't block user flow
        }
    }

    // ===========================================
    // PLATFORM ID EXTRACTION - FIXED VERSION
    // ===========================================

    grabPlatformIds() {
        return {
            fbp: this.getCookie('_fbp'),
            fbc: this.getFacebookClickId(),
            gclid: this.getURLParam('gclid'),
            ttclid: this.getURLParam('ttclid')
        };
    }

    getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
    }

    getURLParam(param) {
        // Cross-browser compatible URL parameter extraction
        const urlParams = window.location.search.substring(1);
        if (!urlParams) return null;
        
        const params = urlParams.split('&');
        for (let i = 0; i < params.length; i++) {
            const pair = params[i].split('=');
            if (pair[0] === param) {
                return pair[1] ? decodeURIComponent(pair[1]) : '';
            }
        }
        return null;
    }

    getFacebookClickId() {
        // First, check if _fbc cookie already exists and is valid
        const existingFbc = this.getCookie('_fbc');
        if (existingFbc && this.isValidFbc(existingFbc)) {
            return existingFbc;
        }
        
        // Check for fbclid in URL parameter
        const fbclid = this.getURLParam('fbclid');
        if (fbclid && this.isValidFbclid(fbclid)) {
            // Create properly formatted fbc value
            const timestamp = Date.now();
            const subdomainIndex = this.getSubdomainIndex();
            const fbc = `fb.${subdomainIndex}.${timestamp}.${fbclid}`;
            
            // Set the _fbc cookie for future use
            this.setFbcCookie(fbc);
            
            return fbc;
        }
        
        return null;
    }

    // Validate fbc format: fb.{subdomain}.{timestamp}.{fbclid}
    isValidFbc(fbc) {
        if (!fbc || typeof fbc !== 'string') return false;
        
        const parts = fbc.split('.');
        if (parts.length !== 4) return false;
        
        const [prefix, subdomain, timestamp, fbclid] = parts;
        
        return (
            prefix === 'fb' &&
            /^\d+$/.test(subdomain) &&
            /^\d{13}$/.test(timestamp) && // 13-digit timestamp
            fbclid && fbclid.length > 0
        );
    }

    // Validate fbclid format (should be alphanumeric string)
    isValidFbclid(fbclid) {
        return fbclid && 
            typeof fbclid === 'string' && 
            fbclid.length > 0 &&
            /^[A-Za-z0-9_-]+$/.test(fbclid);
    }

    // Get subdomain index (usually 1, but can be calculated based on domain)
    getSubdomainIndex() {
        // For most cases, return 1
        // You can implement more complex logic if needed for subdomains
        return 1;
    }

    // Set _fbc cookie with proper attributes
    setFbcCookie(fbc) {
        const expirationDate = new Date();
        expirationDate.setTime(expirationDate.getTime() + (90 * 24 * 60 * 60 * 1000)); // 90 days
        
        document.cookie = `_fbc=${fbc}; expires=${expirationDate.toUTCString()}; path=/; domain=${this.getCookieDomain()}; SameSite=Lax`;
    }

    // Get the appropriate domain for cookie setting
    getCookieDomain() {
        const hostname = window.location.hostname;
        
        // For localhost or IP addresses, don't set domain
        if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
            return hostname;
        }
        
        // For regular domains, use the main domain
        const parts = hostname.split('.');
        if (parts.length >= 2) {
            return `.${parts.slice(-2).join('.')}`;
        }
        
        return hostname;
    }

    getUTMParams() {
        // Cross-browser compatible UTM parameter extraction
        const utmParams = {
            utm_source: this.getURLParam('utm_source'),
            utm_medium: this.getURLParam('utm_medium'),
            utm_campaign: this.getURLParam('utm_campaign'),
            utm_term: this.getURLParam('utm_term'),
            utm_content: this.getURLParam('utm_content')
        };
        
        // Only return UTM params that have values
        const filteredUtmParams = {};
        for (const [key, value] of Object.entries(utmParams)) {
            if (value) {
                filteredUtmParams[key] = value;
            }
        }
        
        return filteredUtmParams;
    }

    sha256(str) {
        return crypto.subtle.digest("SHA-256", new TextEncoder().encode(str))
        .then(buf => Array.from(new Uint8Array(buf))
            .map(x => x.toString(16).padStart(2, '0'))
            .join(''));
       }

    // ===========================================
    // META PIXEL INTEGRATION
    // ===========================================

    sendToMetaPixel(eventName, properties, eventId) {
        // Skip specific events
        const skipEvents = ['scroll_milestone', 'page_visit', 'page_view', 'view_content'];
        if (skipEvents.includes(eventName)) return;


        if (typeof fbq !== 'undefined') {

            const enhancedProperties = {
                ...properties,
                event_id: eventId
            };

            if (eventName === 'email_captured') {
                const email = localStorage.getItem('user_email') || this.uid;
                
                this.sha256(email).then(hashedEmail => {
                    const { email, ...updatedProperties } = properties;

                    fbq('track', 'Subscribe', {
                        ...updatedProperties,
                        event_id: eventId,
                        external_id: this.uid,
                        em: hashedEmail
                    });
                });
            } else if (eventName === 'search_performed') {
                fbq('track', 'Search', {
                    search_string: properties.query || '',
                    event_id: eventId
                });
            } else {
                fbq('trackCustom', eventName, enhancedProperties);
            }
        }
    }

    // ===========================================
    // GOOGLE ANALYTICS 4 INTEGRATION
    // ===========================================

    sendToGA4(eventName, properties) {
        if (typeof gtag !== 'undefined') {

            const ga4EventMap = {
                'view_content': 'view_item',
                'purchase': 'purchase',
                'search_performed': 'search',
                'sustainability_engaged': 'sustainability_engaged',
                'soil_to_soil_engaged': 'soil_to_soil_engaged',
                'social_proof_engaged': 'social_proof_engaged',
                'high_product_engagement': 'high_product_engagement',
                'session_start': 'session_start_custom',
                'add_to_cart': 'add_to_cart_custom',
                'initiate_checkout': 'initiate_checkout_custom'
            };

            const ga4Event = ga4EventMap[eventName] || eventName;

            const ga4Properties = {
                ...properties,
                event_time: Math.floor(Date.now() / 1000),
                event_source: 'website',
                custom_parameters: properties
            };

            if (eventName === 'email_captured') {
                const email = localStorage.getItem('user_email') || this.uid;
                gtag('config', 'G-9DQ4Y3GGCP', {
                    'user_properties': {
                        'email': email,
                        'email_captured': true,
                        'email_capture_date': new Date().toISOString()
                    }
                });
                gtag('event', 'sign_up', {
                    method: properties.source || 'website',
                    email: email,
                    ...ga4Properties
                });
            } else {
                if (ga4Event === 'custom_event') {
                    gtag('event', 'custom_event', {
                        event_name: eventName,
                        ...ga4Properties
                    });
                } else {
                    gtag('event', ga4Event, ga4Properties);
                }
            }
        }
    }

    // ===========================================
    // HELPER UTILITIES
    // ===========================================

    getOrCreateUID() {
        const email = localStorage.getItem('user_email');
        if (email) return email;

        if (typeof Shopify !== 'undefined' && Shopify.customer && Shopify.customer.email) {
            const email = Shopify.customer.email;
            localStorage.setItem('user_email', email);
            return email;
        }

        let uid = localStorage.getItem('ecommerce_uid');
        if (!uid) {
            uid = 'anon_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            localStorage.setItem('ecommerce_uid', uid);
        }
        return uid;
    }

    getOrCreateSessionId() {
        let sessionId = sessionStorage.getItem('session_id');
        if (!sessionId) {
            sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            sessionStorage.setItem('session_id', sessionId);
        }
        return sessionId;
    }

    trackSessionStart() {
        const sessionKey = 'session_start_tracked';
        const hasTrackedThisSession = sessionStorage.getItem(sessionKey);

        if (hasTrackedThisSession) {
            return;
        }

        const lastSessionTime = localStorage.getItem('last_session_time');
        const currentTime = Date.now();
        const sessionTimeout = 30 * 60 * 1000; // 30 minutes
        const isTimedOut = lastSessionTime && (currentTime - parseInt(lastSessionTime)) > sessionTimeout;

        this.track('session_start', {
            is_new_visitor: !localStorage.getItem('has_visited'),
            is_timed_out_session: !!isTimedOut,
            referrer_domain: this.getReferrerDomain(),
            device_type: this.getDeviceType(),
            screen_width: window.screen.width,
            screen_height: window.screen.height,
            viewport_width: window.innerWidth,
            viewport_height: window.innerHeight,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            language: navigator.language,
            platform: (navigator.userAgentData && navigator.userAgentData.platform) || '',
            session_timeout_minutes: sessionTimeout / (60 * 1000)
        });

        sessionStorage.setItem(sessionKey, 'true');
        localStorage.setItem('last_session_time', currentTime.toString());
        localStorage.setItem('has_visited', 'true');
    }

    getReferrerDomain() {
        try {
            return new URL(document.referrer).hostname;
        } catch {
            return '';
        }
    }

    getDeviceType() {
        const ua = navigator.userAgent;
        const screenWidth = window.screen.width;

        if (/iPad/.test(ua)) return 'tablet';
        if (/Mobile|Android|iPhone/.test(ua)) return 'mobile';
        if (screenWidth <= 768) return 'mobile';
        if (screenWidth <= 1024) return 'tablet';
        return 'desktop';
    }
}